import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { API } from "../App";
import { renderMarkdownSafe } from "../lib/safeMarkdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/faqs`)
      .then((res) => {
        if (alive) setFaqs(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load FAQs", err);
        if (alive) setFaqs([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 p-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold logo-text">
            TAXINEARME
          </Link>
          <Link
            to="/"
            className="text-white/60 hover:text-[#C5A059] flex items-center gap-2 text-sm"
            data-testid="faq-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>
      <main
        className="max-w-3xl mx-auto p-6 lg:p-12"
        data-testid="faq-page"
      >
        <div className="mb-10">
          <div className="flex items-center gap-3 text-[#C5A059] mb-3">
            <HelpCircle className="w-6 h-6" />
            <span className="text-xs uppercase tracking-widest">Help center</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
            Frequently Asked Questions
          </h1>
          <p className="text-white/50 mt-4 text-base">
            Everything you need to know about booking, payment, drivers and our
            luxury transfer service.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : faqs.length === 0 ? (
          <div
            className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center text-white/50"
            data-testid="faq-empty"
          >
            <p>No FAQs have been published yet. Please check back soon.</p>
          </div>
        ) : (
          <Accordion
            type="single"
            collapsible
            className="space-y-2"
            data-testid="faq-accordion"
          >
            {faqs.map((faq) => (
              <FAQItemBlock key={faq.faq_id} faq={faq} />
            ))}
          </Accordion>
        )}

        <div className="mt-16 border-t border-white/5 pt-8 text-white/40 text-sm">
          <p>
            Can't find what you're looking for?{" "}
            <Link to="/" className="text-[#C5A059] hover:underline">
              Contact us from the homepage
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}

function FAQItemBlock({ faq }) {
  const html = useMemo(
    () => ({ __html: renderMarkdownSafe(faq.content, "faq-public") }),
    [faq.content]
  );
  return (
    <AccordionItem
      value={faq.faq_id}
      className="bg-[#0F0F0F] border border-white/5 rounded-sm px-5 !border-b"
      data-testid={`faq-item-${faq.faq_id}`}
    >
      <AccordionTrigger className="text-white hover:no-underline text-left text-base font-medium py-5">
        {faq.question}
      </AccordionTrigger>
      <AccordionContent className="text-white/70">
        <article
          className="prose-custom prose-sm"
          dangerouslySetInnerHTML={html}
        />
      </AccordionContent>
    </AccordionItem>
  );
}
